import type { ConnectorFieldSchema, ConnectorTypeSchemaResponse } from '@/types/connector-schema';

export type FieldTranslator = (value: unknown) => Record<string, unknown>;

export interface SectionSpec {
  id: string;
  title: string;
  icon: string;
  description?: string;
  cols?: 1 | 2;
  fields: string[];
  translators?: Record<string, FieldTranslator>;
  syntheticFields?: Record<string, { type: 'queries'; label: string; hint?: string }>;
  /**
   * Optional divider sub-heading rendered ABOVE the keyed field within the
   * section body, keyed by field name. Used to set a field apart from the ones
   * above it without splitting the section (#894 — the google_sheets
   * `include_source_url` "Output" toggle vs the discovery criteria).
   */
  subheadings?: Record<string, string>;
}

// #2190: every `empty_means_all` account field is listed with its hidden
// `<field>_excluded` sibling (the "selected plus any added later" deny-list).
// A hidden field never renders, but onSave only sends dirty fields that appear
// in a section, so a sibling left out here would be silently dropped on save.

// Yahoo DSP moved onto the shared window keys in #2098 (window_mode switch gating
// lookback_days vs start_date/end_date through the backend schema's visible_if).
// The `interval` field #173 kept is gone: nothing read it — YahooDspSettings
// ignored the key, and granularity is interval_type_id (Day), not a form field.
const yahooDsp: SectionSpec[] = [
  // Note: Yahoo DSP credentials are operator-level (env vars), not collected here.
  {
    id: 'accounts',
    title: 'Accounts',
    icon: 'briefcase',
    description: 'Which advertisers each report covers.',
    // #2434: advertiser_scope is the only control. "Only the selected
    // advertisers" is withdrawn (#2350), with its Load Advertisers button and
    // Advertisers picker; a stored account_ids is never sent, so it is kept as
    // stored. A stored "selected" shows the yahoo_dsp advertiser_scope notice.
    fields: ['advertiser_scope'],
  },
  {
    id: 'reporting',
    title: 'Report shape',
    icon: 'bar-chart-3',
    description: 'Dimensions become rows; metrics become columns.',
    // #936: report_resource (required) gates the custom dimension/metric pickers
    // (both carry visible_if report_resource=custom); order matches the backend
    // data_config group (report_resource 0, dimension_ids 1, metric_ids 2).
    fields: ['report_resource', 'dimension_ids', 'metric_ids'],
  },
  {
    id: 'window',
    title: 'Reporting window',
    icon: 'calendar-range',
    description: 'Time range and timezone for each scheduled pull.',
    cols: 2,
    fields: ['timezone', 'window_mode', 'lookback_days', 'start_date', 'end_date'],
  },
];

const googleAds: SectionSpec[] = [
  {
    id: 'auth',
    title: 'Authentication',
    icon: 'key-round',
    description: 'OAuth-connected Google account with access to the customer ID below.',
    fields: ['oauth_connect'],
  },
  {
    id: 'accounts',
    title: 'Customer account',
    icon: 'briefcase',
    description: 'Google Ads customer account to ingest. Use login_customer_id when accessing through a manager (MCC) account.',
    fields: ['customer_id', 'login_customer_id', 'customer_ids', 'delivery_probe', 'always_pull_ids'],
  },
  {
    id: 'reporting',
    title: 'Resources',
    icon: 'bar-chart-3',
    description: 'Which Google Ads resources to pull on every run.',
    fields: ['resources'],
  },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    // #929: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days/start_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'start_date', 'timezone', 'invoice_lookback_months'] },
];

const hubspot: SectionSpec[] = [
  {
    id: 'auth',
    title: 'Authentication',
    icon: 'key-round',
    description: 'Connect a HubSpot portal. The OAuth popup captures the portal ID and tokens.',
    fields: ['oauth_connect'],
  },
  {
    id: 'reporting',
    title: 'Objects',
    icon: 'package',
    description: 'CRM objects to sync into the lakehouse.',
    fields: ['objects', 'include_custom_objects'],
  },
  { id: 'window', title: 'Initial backfill window', icon: 'calendar-range', cols: 2,
    description: 'Used only on the first run; subsequent runs resume from the last successful run.',
    // #929: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days/start_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'start_date', 'timezone'] },
];

const googleAnalytics: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected Google account with access to the GA4 properties below.',
    fields: ['oauth_connection'] },
  { id: 'properties', title: 'Properties', icon: 'briefcase',
    description: 'GA4 properties to ingest.',
    fields: ['property_ids', 'property_ids_excluded', 'delivery_probe', 'always_pull_ids'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Which GA4 resources to pull on every run.',
    // #2273: the properties the Period Users resource asks about; #2482: the
    // properties the two Geo City resources ask about.
    fields: ['selected_resources', 'period_users_property_ids', 'geo_city_property_ids'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    // #929: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days/start_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'start_date', 'interval'] },
];

const bingAds: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected Microsoft account with access to the customers below.',
    fields: ['oauth_connect', 'customer_ids', 'customer_ids_excluded'] },
  // #938: account_ids (the #511 "Account Allow-list") was declared in the backend
  // schema but never curated here, so the editor offered no way to narrow an
  // extract and every connector pulled ALL accounts under the selected customers.
  // It is `multi` without auto_select -- leaving it empty means "extract ALL".
  { id: 'accounts', title: 'Ad accounts', icon: 'briefcase',
    description: 'Optionally narrow extraction to specific ad accounts under the customers above.',
    fields: ['account_ids', 'delivery_probe', 'always_pull_ids'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Bing Ads entities and report types to pull on every run.',
    // #2478: the accounts the Geographic City Performance Report is pulled for.
    fields: ['resources', 'city_account_ids'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    description: 'First-run lookback, or an explicit date range to re-pull.',
    // #929: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days vs start_date/end_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'start_date', 'end_date'] },
];

const snapchatAds: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected Snapchat account.',
    fields: ['oauth_connect'] },
  // #939: account_config group -- both are oauth_populated_dropdowns fed by
  // /connectors/snapchat_ads/oauth/accounts, and the adapter filters the
  // organizations/ad_accounts resources by them. Order mirrors the backend
  // (organizations contain ad accounts).
  { id: 'accounts', title: 'Accounts', icon: 'briefcase',
    description: 'Snapchat organizations and ad accounts to ingest.',
    fields: ['organization_ids', 'organization_ids_excluded', 'ad_account_ids',
      'ad_account_ids_excluded', 'delivery_probe', 'always_pull_ids'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Snapchat Ads resources to pull on every run.',
    fields: ['selected_resources'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    // #933: window_mode is present but SINGLE-option (lookback only, Q2
    // decision) -- no fixed_start alternative, no start_date/end_date fields.
    // #1265: the two per-granularity stats lookbacks landed in the backend
    // schema with #1148 but were never curated here, so they could never
    // render and the #935 drift guard went red on dev-main. Order mirrors the
    // backend data_config group (window_mode 2, lookback_days 3,
    // stats_daily 4, stats_hourly 5, interval 6); both stats fields carry the
    // same visible_if window_mode='lookback' gate as lookback_days.
    fields: ['window_mode', 'lookback_days', 'stats_daily_lookback_days',
             'stats_hourly_lookback_days', 'interval'] },
];

const twitterAds: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected X/Twitter Ads account.',
    fields: ['oauth_connect'] },
  { id: 'accounts', title: 'Accounts', icon: 'briefcase',
    description: 'Ad accounts to ingest.',
    fields: ['account_ids', 'account_ids_excluded'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Which X/Twitter Ads resources to pull on every run.',
    fields: ['resources'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    // #933: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days vs start_date/end_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'start_date', 'end_date', 'granularity', 'timezone'] },
];

const redditAds: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected Reddit Ads account.',
    fields: ['oauth_connect'] },
  { id: 'accounts', title: 'Accounts', icon: 'briefcase',
    description: 'Reddit ad accounts to ingest.',
    fields: ['account_ids', 'account_ids_excluded'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Which Reddit Ads resources to pull on every run.',
    fields: ['resources'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    // #933: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days vs start_date/end_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'start_date', 'end_date'] },
];

// Amazon DSP keeps its native `interval` + `timezone` + `start_date`/`end_date` window
// shape (#173; Yahoo DSP shared it until #2098).
// The sections.test.ts pin guards against accidental migration to the synthetic
// `date_range` pattern.
const amazonDsp: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected Amazon advertising account.',
    fields: ['oauth_connect'] },
  { id: 'accounts', title: 'Advertisers', icon: 'briefcase',
    description: 'Amazon DSP advertiser accounts to ingest.',
    // #2480: dsp_advertiser_ids narrows the report filter to named DSP advertisers.
    fields: ['advertiser_ids', 'advertiser_ids_excluded', 'delivery_probe', 'always_pull_ids', 'dsp_advertiser_ids'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Which Amazon DSP resources to pull on every run.',
    fields: ['selected_resources'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    description: 'Time range, granularity, and timezone for each scheduled pull.',
    // window_mode / lookback_days / report_lag_days added by #2008: before it,
    // the report window was hard-coded in the task and these controls bound the
    // invoice pull alone. start_date/end_date now gate on window_mode rather
    // than interval, so sectionVisibleFields hides them outside fixed_start.
    fields: ['interval', 'timezone', 'window_mode', 'lookback_days',
             'report_lag_days', 'start_date', 'end_date'] },
];

const basisAds: SectionSpec[] = [
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Basis Ads resources to pull on every run. Authentication is configured at the operator level.',
    fields: ['resources'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    fields: ['stats_start_date', 'stats_end_date'] },
];

const linkedinAds: SectionSpec[] = [
  {
    id: 'auth',
    title: 'Authentication',
    icon: 'key-round',
    description: 'OAuth-connected LinkedIn account with access to the ad accounts below.',
    fields: ['oauth_connect'],
  },
  {
    id: 'accounts',
    title: 'Ad accounts',
    icon: 'briefcase',
    description: 'LinkedIn ad accounts to ingest. Auto-discovered after OAuth.',
    fields: ['account_ids', 'account_ids_excluded', 'delivery_probe', 'always_pull_ids'],
  },
  {
    id: 'reporting',
    title: 'Resources',
    icon: 'bar-chart-3',
    description: 'Which LinkedIn Ads resources to pull on every run.',
    // #1913: analytics_pivots sits beside `resources` rather than in the
    // window section because it is the same KIND of choice -- what to pull,
    // not when. The backend schema's visible_if hides it unless `analytics`
    // is among the selected resources, so the two read as one decision.
    // Without a section entry the field is served but can never render,
    // which is the gap test_connector_sections_schema_drift exists to catch.
    fields: ['resources', 'analytics_pivots'],
  },
  {
    id: 'window',
    title: 'Analytics window',
    icon: 'calendar-range',
    cols: 2,
    // #826: start_date/end_date re-pull overrides (parity with bing_ads).
    // #933: window_mode switch (rolling lookback vs a fixed start date) gates
    // lookback_days vs start_date/end_date via the backend schema's visible_if.
    fields: ['window_mode', 'lookback_days', 'interval', 'start_date', 'end_date'],
  },
];

// Group F connectors (#228). Authored directly from the backend schema in
// src/lakehouse/connectors/schemas/connector_schemas.py, dropping infra/throughput
// fields (batch_size, output_format, report_row_limit) — same pattern as the
// connectors above. The legacy `beeswax` type is deliberately NOT curated here
// (superseded by the two split types below); it falls back to defaultSections.
const metaAds: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'OAuth-connected Meta account with access to the ad accounts below.',
    fields: ['oauth_connection'] },
  { id: 'accounts', title: 'Ad accounts', icon: 'briefcase',
    description: 'Meta ad accounts to ingest. Auto-discovered after OAuth.',
    fields: ['ad_account_ids', 'ad_account_ids_excluded', 'delivery_probe', 'always_pull_ids', 'account_concurrency'] },
  { id: 'reporting', title: 'Insights', icon: 'bar-chart-3',
    description: 'Insight levels, metrics, and optional breakdown dimensions to pull on every run.',
    fields: ['levels', 'fields', 'breakdowns', 'include_unbroken_totals'] },
  { id: 'window', title: 'Reporting window', icon: 'calendar-range', cols: 2,
    description: 'Granularity and how many days back each scheduled pull covers.',
    fields: ['time_increment', 'date_range_days'] },
  { id: 'objects', title: 'Catalog objects', icon: 'layers',
    description: 'Optional metadata catalogs to pull alongside insights.',
    fields: ['include_objects', 'include_creatives', 'include_targeting',
             'include_custom_conversions', 'include_pixels', 'include_invoices',
             'include_invoice_line_items'] },
];

const tiktokAds: SectionSpec[] = [
  { id: 'auth', title: 'Authentication', icon: 'key-round',
    description: 'Connect with TikTok Ads via OAuth, or check the box to paste a long-lived access token instead.',
    // #937: oauth_connect (required OAuth button) + show_manual_credentials were
    // absent from this curated list, so the editor could not (re-)authorize a
    // TikTok connector -- only a manually pasted token. access_token is gated by
    // the show_manual_credentials toggle via the backend schema's visible_if.
    fields: ['oauth_connect', 'show_manual_credentials', 'access_token'] },
  { id: 'accounts', title: 'Advertisers', icon: 'briefcase',
    description: 'TikTok advertiser IDs to ingest, one per line.',
    fields: ['advertiser_ids', 'advertiser_ids_excluded', 'delivery_probe', 'always_pull_ids'] },
  { id: 'reporting', title: 'Resources', icon: 'bar-chart-3',
    description: 'Which TikTok Ads resources to pull on every run.',
    fields: ['resources'] },
  { id: 'window', title: 'Backfill window', icon: 'calendar-range',
    description: 'Report lookback re-pulls older report dates each run (raise it to backfill past the checkpoint); billing lookback covers Business Center invoices and transactions. Set both report start/end dates for an exact historical re-pull (overrides report lookback); clear them to resume the rolling lookback.',
    // #933: window_mode switch (rolling lookback vs a fixed start date) gates
    // report_lookback_days vs report_start_date/report_end_date via the
    // backend schema's visible_if. Scoped to the REPORT window only --
    // lookback_days (Business Center billing) is deliberately ungated.
    fields: ['window_mode', 'report_lookback_days', 'report_start_date', 'report_end_date', 'lookback_days'] },
];

// Google Sheets is deliberately NOT curated here (#994). It renders via the
// defaultSections(schema) fallback, straight from GOOGLE_SHEETS_SCHEMA — the SAME
// source the create wizard uses — so the configuration page and the wizard show
// the same sections, in the same order, with the same titles/descriptions, by
// construction. Its schema declares dedicated Output / Sync groups (#994) and
// carries per-group icon/description (#898), and defaultSections now forwards
// those; hidden fields (batch_size, output_format) are dropped by
// sectionVisibleFields. A curated list would only reintroduce the wizard-vs-editor
// drift this ticket removed. test_connector_sections_schema_drift.py guards that
// google_sheets stays schema-derived and has no ungrouped fields.

const beeswaxAnalytics: SectionSpec[] = [
  { id: 'connection', title: 'Connection', icon: 'database', cols: 2,
    description: 'Buzz API host and the credentials used for session-cookie auth.',
    fields: ['buzz_host', 'email', 'password'] },
  { id: 'reporting', title: 'Report', icon: 'bar-chart-3',
    description: 'Report view, the dimensions and metrics to extract, and which advertiser to scope to.',
    // #1100: report_resource preset picker leads; report_view/dimensions/metrics
    // carry visible_if report_resource=custom and render only under Custom (cf.
    // the Yahoo DSP report_resource precedent at the top of this file).
    // #1990: report_advertiser_id sits with the report rather than in its own
    // section -- it changes WHAT the report returns, not when it runs, and the
    // backend rejects a config that sets it without advertiser_id among the
    // selected dimensions, so the two belong next to each other on screen.
    // #1999: the loader button immediately precedes the picker it fills, so
    // "Load Advertisers" reads as the instruction for the control beneath it.
    fields: ['report_resource', 'report_view', 'report_dimensions', 'report_metrics', 'load_beeswax_advertisers', 'report_advertiser_id'] },
  { id: 'window', title: 'Backfill window', icon: 'calendar-range', cols: 2,
    description: 'Default lookback applied to scheduled runs.',
    // #933: window_mode switch (rolling lookback vs a fixed start date) gates
    // report_lookback_days vs report_start_date/report_end_date via the
    // backend schema's visible_if.
    fields: ['window_mode', 'report_lookback_days', 'report_start_date', 'report_end_date'] },
];

export const SECTIONS_BY_TYPE: Record<string, SectionSpec[]> = {
  yahoo_dsp: yahooDsp,
  google_ads: googleAds,
  hubspot,
  google_analytics: googleAnalytics,
  bing_ads: bingAds,
  snapchat_ads: snapchatAds,
  twitter_ads: twitterAds,
  reddit_ads: redditAds,
  amazon_dsp: amazonDsp,
  linkedin_ads: linkedinAds,
  basis_ads: basisAds,
  meta_ads: metaAds,
  tiktok_ads: tiktokAds,
  // google_sheets intentionally omitted — schema-derived via defaultSections (#994).
  beeswax_analytics: beeswaxAnalytics,
};

export function defaultSections(schema: ConnectorTypeSchemaResponse): SectionSpec[] {
  const groups = [...(schema.field_groups || [])].sort((a, b) => a.order - b.order);
  return groups.map((g) => {
    const fields = schema.fields
      .filter((f) => f.group === g.name)
      .sort((a, b) => a.order - b.order)
      .map((f) => f.name);
    // Carry the group's own icon + description through so a schema-derived
    // section renders the same header as the wizard's FieldGroupSection, which
    // reads group.icon / group.description (#994). Falls back to 'settings' for
    // a group with no icon.
    return { id: g.name, title: g.label, icon: g.icon ?? 'settings', description: g.description, fields };
  });
}

/**
 * The subset of a section's fields that should render given the current form
 * values. A field renders unless its backend `visible_if` rule fails (every key
 * must equal the corresponding live value). Synthetic fields have no schema and
 * always render. Fields that are neither a schema field nor a synthetic are
 * dropped. This is the single source of truth for field-level conditional
 * rendering, shared by SectionBody (which fields to draw) and
 * SectionedConfigurationForm (which sections are non-empty, mirroring the
 * wizard dropping empty groups).
 */
export function sectionVisibleFields(
  section: SectionSpec,
  schemaFields: ConnectorFieldSchema[],
  values: Record<string, unknown>,
): string[] {
  const byName = new Map(schemaFields.map((f) => [f.name, f]));
  return section.fields.filter((name) => {
    const synthetic = section.syntheticFields?.[name];
    const schemaField = byName.get(name);
    if (!synthetic && !schemaField) return false;
    // A hidden field is never rendered on either surface (mirrors the wizard's
    // isFieldVisible, which returns false for field.hidden before visible_if).
    // Required for the schema-derived path so hidden fields like batch_size /
    // output_format aren't surfaced on the config page (#994).
    if (schemaField?.hidden) return false;
    if (schemaField?.visible_if) {
      return Object.entries(schemaField.visible_if).every(
        ([k, v]) => values[k] === v,
      );
    }
    return true;
  });
}
